import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { AppConfigService } from "@app/core/services/app-config.service";
import { PlatformTypes } from "@app/models";
import { PlatformAccountService } from "@app/services";
import { PlatformAccountGateService } from "@app/shared/components/advanced-account-setup-dialog";
import { TranslocoService } from "@ngneat/transloco";
import {
  ExternalFeatureDependency,
  ExternalFeatureWithDependencyGetDto,
  PermissionTypeDto,
  SitesBuilderService,
  UrlOpenModeDto
} from "clearline-api";
import { UserPermissionService } from "clearline-common";
import { map, take } from "rxjs/operators";
import { ConfirmDialogComponent, ConfirmDialogOptions } from "ui-kit";
import { SidebarAction, SidebarItemOptions, SidebarItemOptionsParam } from "../menus";

@Injectable({
  providedIn: "root"
})
export class SidebarActionsService {
  readonly digitalScreensDashboardLink = "http://app.clearlinetv.com/";

  constructor(
    private appConfigService: AppConfigService,
    private dialog: MatDialog,
    private platformAccountService: PlatformAccountService,
    private platformAccountGateService: PlatformAccountGateService,
    private router: Router,
    private sitesBuilderService: SitesBuilderService,
    private transloco: TranslocoService,
    private userPermissionService: UserPermissionService
  ) {}

  executeAction(action: SidebarAction, options?: SidebarItemOptions, icon?: string): void {
    const params = options?.params;

    switch (action) {
      case SidebarAction.OpenPlatform:
        this.openPlatform(params as PlatformTypes);
        break;
      case SidebarAction.OpenExternalFeature:
        this.openExternalFeature(params);
        break;
      case SidebarAction.OpenExternalFeatureByPermission:
        this.openExternalFeatureByPermission(options.permission, icon);
        break;
      case SidebarAction.OpenCmc:
        this.openCmc();
        break;
      case SidebarAction.OpenDigitalScreensDashboard:
        this.openDigitalScreensDashboard();
        break;
      case SidebarAction.OpenHyperLink:
        this.openHyperLink(options?.hyperLink, options?.newTab);
        break;
    }
  }

  openPlatform(platformId: PlatformTypes): void {
    this.platformAccountService.openPlatform(platformId);
  }

  openExternalFeature(param: SidebarItemOptionsParam): void {
    const request$ =
      param === SidebarItemOptionsParam.SitesBuilder
        ? this.sitesBuilderService.getTargetUrl().pipe(map(({ url }) => url))
        : this.platformAccountService.getExternalFeatureByCode(param as string);
    request$.pipe(take(1)).subscribe((targetUrl) => {
      if (targetUrl) {
        window.open(targetUrl, "_blank");
      } else {
        this.processExternalFeatureUrlError();
      }
    });
  }

  openExternalFeatureByPermission(permission?: PermissionTypeDto, icon?: string): void {
    const externalFeature: ExternalFeatureWithDependencyGetDto | null =
      this.userPermissionService.externalFeaturesMap.get(permission) || null;

    if (!externalFeature || !this.platformAccountGateService.isProvisioned(externalFeature.platformId)) {
      this.openExternalFeatureWithDependency(externalFeature, permission);
      return;
    }

    this.platformAccountGateService.open({
      platformId: externalFeature.platformId,
      icon,
      open: () => this.openExternalFeatureWithDependency(externalFeature, permission)
    });
  }

  openCmc(): void {
    window.open(this.appConfigService.appData.marketingCenterUrl, "_blank");
  }

  openDigitalScreensDashboard(): void {
    window.open(this.digitalScreensDashboardLink, "_blank");
  }

  openHyperLink(url?: string, newTab = true): void {
    if (url) {
      window.open(url, newTab ? "_blank" : "_self");
    }
  }

  private openExternalFeatureWithDependency(
    externalFeature: ExternalFeatureWithDependencyGetDto | null,
    permission?: PermissionTypeDto
  ): void {
    const dependency = this.getActualDependency(externalFeature);

    if (!externalFeature || dependency !== ExternalFeatureDependency.None) {
      this.processExternalFeatureUrlError(dependency);
    } else {
      this.processExternalFeatureUrlOpening(externalFeature, permission);
    }
  }

  private getActualDependency(externalFeature: ExternalFeatureWithDependencyGetDto | null): ExternalFeatureDependency | undefined {
    const dependency = externalFeature?.dependency;
    const isPlatformChecked =
      dependency === ExternalFeatureDependency.Platform && this.platformAccountGateService.isProvisioned(externalFeature.platformId);

    return isPlatformChecked ? ExternalFeatureDependency.None : dependency;
  }

  private processExternalFeatureUrlOpening(externalFeature: ExternalFeatureWithDependencyGetDto, permission?: PermissionTypeDto): void {
    const { url, urlOpenMode } = externalFeature;

    if (!urlOpenMode || !url || !permission) {
      this.processExternalFeatureUrlError();
      return;
    }

    if (urlOpenMode === UrlOpenModeDto.NewTab) {
      window.open(url, "_blank");
    } else if (urlOpenMode === UrlOpenModeDto.IFrame) {
      void this.router.navigate(["manager/external-feature"], { queryParams: { target: permission } });
    }
  }

  private processExternalFeatureUrlError(dependency?: ExternalFeatureDependency): void {
    const i18nKey = this.getExternalFeatureErrorMessage(dependency);
    const message = this.transloco.translate(i18nKey);

    const options: ConfirmDialogOptions = {
      title: this.transloco.translate(`sidebar.externalFeatureErrorMessage.title`),
      description: message,
      confirmBtnLabel: this.transloco.translate(`common.controls.confirm`)
    };

    this.dialog
      .open(ConfirmDialogComponent, {
        panelClass: "modal-standard-s",
        disableClose: true,
        data: options
      })
      .afterClosed()
      .subscribe();
  }

  private getExternalFeatureErrorMessage(dependency?: ExternalFeatureDependency): string {
    const prefix = `sidebar.externalFeatureErrorMessage`;

    switch (dependency) {
      case ExternalFeatureDependency.Platform:
        return `${prefix}.Platform`;
      case ExternalFeatureDependency.Pos:
        return `${prefix}.Pos`;
      case ExternalFeatureDependency.Widget:
        return `${prefix}.Widget`;
      default:
        return `${prefix}.notConfigured`;
    }
  }
}
